/**
 * Launcher client data — the tenant's curated app grid for THIS caller.
 *
 * Modelled on `useProjects` (features/files/ui/home/data.ts): one query, a
 * 30 s staleTime matching the drive's list-cache contract, and a session-wide
 * realtime subscriber (`LauncherCacheSync`) that invalidates rather than
 * patches. Patching is deliberately
 * NOT done here: a tile's presence is a permission decision the server makes
 * (plan §2), and no client-side patch can re-derive it — `file:updated` says
 * the row changed, never whether the caller may still read it.
 */
import { useEffect } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { request } from '@papan/api-client'
import { useRealtimeOptional, type FileEvent } from '@papan/realtime/client'
import { binNudgeSharedIn, isBinNudge } from '@papan/types'
import {
  presentsAsMiniapp,
  isMiniappFolder,
  isSharedPointer,
  stripPointerSuffix,
  createAccessBurst,
  changesViewerAccess,
} from '@papan/types'

/** One tile. Mirrors `LauncherShortcutDto` in features/launcher/api/routes.ts. */
export interface LauncherShortcut {
  fileId: string
  /** Admin's caption override, or null — fall back to `name`. */
  label: string | null
  position: number
  /** The app's own display name, pointer suffix and `.miniapp` already stripped. */
  name: string
  /** `{ miniappIcon, miniappIconRev }` JSON, or null for the default squircle. */
  icon: string | null
  /** `.shared` mount of another tenant's app — see the DTO's note: the
   *  composited icon PNG is unreachable for these, so the view must not ask. */
  mounted: boolean
}

/**
 * Keyed on tenant AND identity, both load-bearing.
 *
 * Nothing clears the query cache on a tenant switch (`switchTenant` updates
 * refs and navigates; the deck windows remount but the cache survives), so a
 * bare `['launcher','shortcuts']` would paint the PREVIOUS org's app grid for
 * one render after switching. The identity is in the key for the same reason
 * one step down: which apps a member can read is resolved against
 * `auth.orgEmail`, so two seats in the same org legitimately see two different
 * grids.
 */
export const launcherKeys = {
  all: ['launcher'] as const,
  shortcuts: (tenantId: string, orgEmail?: string | null) =>
    ['launcher', 'shortcuts', tenantId, orgEmail ?? ''] as const,
}

/**
 * Does this file event have any chance of changing the grid?
 *
 * Two independent reasons it might, and BOTH are needed:
 *  - the row is already a tile (a curated app renamed off `.miniapp`, deleted,
 *    or re-iconed) — caught by id, because the event carries the row's NEW
 *    name, which by then no longer looks like an app;
 *  - the row looks like an app but isn't a tile yet (a curated app restored
 *    from the bin) — caught by name.
 *
 * Exported for its unit test: the failure mode on either arm is a silently
 * stale grid, which no rendering test can see.
 */
export function eventTouchesTheGrid(e: FileEvent, tileIds: Set<string>): boolean {
  if (tileIds.has(e.fileId)) return true
  const name = e.file?.name ?? e.name
  // No name at all (very old publisher): can't reason, so refetch. Cheap, and
  // the alternative is a silently stale grid.
  if (!name) return true
  if (e.file) return presentsAsMiniapp(name, e.file.fileType, e.file.isFolder)
  // Row payload absent (legacy publisher / ambiguous delete): decide on the
  // name alone. `presentsAsMiniapp` cannot take its pointer arm without a
  // `fileType`, so spell both shapes out rather than silently missing every
  // mounted `X.miniapp.shared`.
  return isMiniappFolder(name) || (isSharedPointer(name) && isMiniappFolder(stripPointerSuffix(name)))
}

/**
 * #1141 I1: a FOLDER moved (a drive rename arrives as a move), restored or
 * deleted may hold a curated app BELOW it, and its event names only itself —
 * `eventTouchesTheGrid` can't see that. No delete publisher carries the row, so a
 * delete of unknown kind counts too. Only a MAYBE: marked through
 * `MAY_HOLD_WINDOW_MS`, never one refetch per event.
 */
export function eventMayHoldApps(e: FileEvent): boolean {
  if (e.type !== 'file:moved' && e.type !== 'file:restored' && e.type !== 'file:deleted') return false
  return !e.file || e.file.isFolder
}

/** The window a "may hold an app" mark waits in — the same 5 s the drive's Recent uses for the same reason (a delete burst). */
export const MAY_HOLD_WINDOW_MS = 5000

/** Every cached grid — any tenant, any identity — paired with its key. */
function cachedGrids(qc: QueryClient) {
  return qc.getQueriesData<LauncherShortcut[]>({ queryKey: [...launcherKeys.all, 'shortcuts'] })
}

/**
 * Keeps the grid honest without a reload, for the whole signed-in session.
 *
 * Mounted ONCE beside `FileListCacheSync` (apps/web App.tsx), not inside
 * `useLauncherShortcuts`: a subscription that lives only while the launcher is
 * on screen drops every event that arrives while you are on another page, and
 * the grid was then served stale until its 30 s `staleTime` ran out (#1058, the
 * #956 class). It lives here rather than in `features/files` so that feature
 * never imports this one.
 *
 * Marks stale, never patches: a tile's presence is a permission decision the
 * server makes. The grid on screen refetches now, a cached one on its next
 * read.
 *  - `file:*`: each cached grid the event touches (`eventTouchesTheGrid`, with
 *    THAT grid's tile ids).
 *  - `permission:changed` is the one that matters most and has no file event
 *    behind it: revoking a member's access to a curated app changes nothing
 *    about the file, so a file-only subscription would leave a tile on screen
 *    that 403s when tapped. The signal is a routing hint (scope + folder, never
 *    emails), so every grid refetches — the server re-resolves the
 *    authoritative answer.
 *  - `resync` (events missed during a reconnect): every grid.
 */
export function LauncherCacheSync(): null {
  const qc = useQueryClient()
  const rt = useRealtimeOptional()
  const onFileEvent = rt?.onFileEvent
  const onPermissionChanged = rt?.onPermissionChanged
  const onResync = rt?.onResync
  useEffect(() => {
    if (!onFileEvent || !onPermissionChanged || !onResync) return
    const all = () => { void qc.invalidateQueries({ queryKey: [...launcherKeys.all, 'shortcuts'] }) }
    // #1141 I1 "maybe" marks, coalesced per grid key over MAY_HOLD_WINDOW_MS (a window,
    // not a debounce: a steady stream still flushes). On unmount (tenant switch,
    // sign-out) the pending ones are applied WITHOUT refetching — never a refetch
    // under the next scope.
    const pending = new Map<string, readonly unknown[]>()
    let timer: ReturnType<typeof setTimeout> | null = null
    const flush = (refetchType: 'active' | 'none') => {
      timer = null
      for (const key of pending.values()) void qc.invalidateQueries({ queryKey: key, exact: true, refetchType })
      pending.clear()
    }
    const markMaybe = (key: readonly unknown[]) => {
      pending.set(JSON.stringify(key), key)
      if (!timer) timer = setTimeout(() => flush('active'), MAY_HOLD_WINDOW_MS)
    }
    // #1209: tagged-true nudges react through their own burst — the first ~300 ms later, then at most one grid
    // refetch per interval: a source binning or restoring N shared folders sends N of them to every recipient tab.
    const nudgeBurst = createAccessBurst(all)
    const offFile = onFileEvent((e) => {
      // A Bin-only nudge (#1058 review) carries ANOTHER tenant's id, so no tile
      // id can match it — but its name can: a source org restoring a shared
      // `X.pdapp` reaches this drive only as a nudge, and is the one signal its
      // `.shared` tile has a live target again. Match those by name alone — any
      // app-looking name, not only listed tiles: a tile whose target is gone may
      // not be listed at all, and an app-named delete/restore is rare enough
      // that the odd extra refetch is the cheaper error (code review).
      const nudge = isBinNudge(e)
      // #1209: the server says whether the binned / restored subtree held a share to THIS drive — exact for an
      // ancestor of a shared app and for one renamed since (no name matches those), and the only signal when the
      // recipient's last share from the source was dropped (that nudge carries no name). Trusted either way; the
      // name match below is only for a nudge without the tag (a server older than #1209, or one that couldn't tell).
      // Tiles are only native `.pdapp` folders and `.shared` pointers to one (`presentsAsMiniapp` leaves `.chain`
      // out on purpose), so every tile's target IS a share root: a `false` tag never hides a tile change.
      const sharedIn = binNudgeSharedIn(e)
      if (sharedIn !== undefined) {
        if (sharedIn) nudgeBurst.push()
        return
      }
      // A nameless nudge (some delete publishers send none) says nothing a grid
      // could match, and nudges fan out to EVERY tenant holding a grant from
      // the source — `eventTouchesTheGrid`'s "no name → refetch" would refetch
      // every grid there on any such delete.
      if (nudge && !(e.file?.name ?? e.name)) return
      for (const [key, tiles] of cachedGrids(qc)) {
        if (eventTouchesTheGrid(e, nudge ? new Set() : new Set((tiles ?? []).map(t => t.fileId)))) {
          void qc.invalidateQueries({ queryKey: key, exact: true })
        } else if (!nudge && eventMayHoldApps(e)) {
          markMaybe(key)
        }
      }
    })
    // At most one grid refetch per interval (`createAccessBurst`): a team delete publishes up to
    // 512 permission changes, each to every member's tab (I261). Not at all for a change naming
    // another member — the grid is the apps THIS viewer may open (#1259).
    const permBurst = createAccessBurst(all)
    const offPerm = onPermissionChanged((event) => { if (changesViewerAccess(event)) permBurst.push() })
    const offResync = onResync((kind) => { if (kind === 'file') all() })
    return () => {
      offFile(); offPerm(); offResync()
      if (timer) { clearTimeout(timer); flush('none') }
      // Unmounted mid-interval (tenant switch, sign-out): mark, never refetch under the next scope.
      const permPending = permBurst.cancel()
      if (nudgeBurst.cancel() || permPending) void qc.invalidateQueries({ queryKey: [...launcherKeys.all, 'shortcuts'], refetchType: 'none' })
    }
  }, [onFileEvent, onPermissionChanged, onResync, qc])
  return null
}

/**
 * The caller's own view of the tenant's curated launcher.
 *
 * `enabled` holds the fetch until the shell knows which tenant it is acting as
 * — the response is tenant-scoped, and a request fired mid-switch would cache
 * a grid under the wrong key.
 */
export function useLauncherShortcuts(opts: {
  tenantId: string
  orgEmail?: string | null
  enabled?: boolean
}) {
  const { tenantId, orgEmail, enabled = true } = opts
  return useQuery<LauncherShortcut[]>({
    queryKey: launcherKeys.shortcuts(tenantId, orgEmail),
    queryFn: async () => {
      const r = await request<{ shortcuts: LauncherShortcut[] }>('/api/launcher/shortcuts')
      return r.shortcuts
    },
    staleTime: 30_000,
    enabled: enabled && !!tenantId,
  })
}
